import { TIME_REGEX } from './constants'

/**
 * Converts a 12-hour time format to 24-hour format
 * @param time - Time in 12-hour format (e.g., "9:00")
 * @param period - AM or PM
 * @returns Time in 24-hour format (e.g., "09:00")
 * @throws Error if time format is invalid
 */
export function convertTo24Hour(time: string, period: 'AM' | 'PM'): string {
  if (!TIME_REGEX.test(time)) {
    throw new Error(`Invalid time format: ${time}`)
  }

  const [hours, minutes] = time.split(':').map(Number)
  let hour24 = hours

  if (period === 'PM' && hours !== 12) hour24 += 12
  if (period === 'AM' && hours === 12) hour24 = 0

  return `${hour24.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`
}

/**
 * Converts a 24-hour time format to 12-hour format
 * @param time24 - Time in 24-hour format (e.g., "09:00")
 * @returns Object containing time in 12-hour format and period (AM/PM)
 * @throws Error if time format is invalid
 */
export function convertTo12Hour(time24: string): {
  time: string
  period: 'AM' | 'PM'
} {
  const match = time24.match(/^([01]?[0-9]|2[0-3]):([0-5][0-9])$/)
  if (!match) {
    throw new Error(`Invalid 24-hour time format: ${time24}`)
  }

  const [, hours, minutes] = match
  const hourNum = parseInt(hours, 10)
  const period: 'AM' | 'PM' = hourNum >= 12 ? 'PM' : 'AM'
  const hours12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum

  return {
    time: `${hours12}:${minutes}`,
    period,
  }
}

/**
 * Validates if a time string is in correct 12-hour format
 * @param time - Time string to validate
 * @returns boolean indicating if time format is valid
 */
export function isValidTimeFormat(time: string): boolean {
  return TIME_REGEX.test(time)
}

/**
 * Converts a time to minutes since midnight for comparison
 * @param time - Time in 12-hour format
 * @param period - AM or PM
 * @param isEndTime - Whether this is an end time (treat 12:00 AM as 1440 if true)
 * @returns Number of minutes since midnight
 */
export function timeToMinutes(
  time: string,
  period: 'AM' | 'PM',
  isEndTime = false,
): number {
  const [hours, minutes] = time.split(':').map(Number)
  let hour24 = hours
  if (period === 'PM' && hours !== 12) hour24 += 12
  if (period === 'AM' && hours === 12) hour24 = 0
  let mins = hour24 * 60 + minutes
  // Special case: treat 12:00 AM as 1440 (end of day) if it's an end time
  if (isEndTime && time === '12:00' && period === 'AM') {
    mins = 24 * 60
  }
  return mins
}

/**
 * Validates if a time range is valid (end time is after start time)
 * @param startTime - Start time in 12-hour format
 * @param startPeriod - Start period (AM/PM)
 * @param endTime - End time in 12-hour format
 * @param endPeriod - End period (AM/PM)
 * @returns boolean indicating if time range is valid
 */
export function isValidTimeRange(
  startTime: string,
  startPeriod: 'AM' | 'PM',
  endTime: string,
  endPeriod: 'AM' | 'PM',
): boolean {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false
  }
  const startMinutes = timeToMinutes(startTime, startPeriod)
  const endMinutes = timeToMinutes(endTime, endPeriod, true)
  return startMinutes < endMinutes
}

/**
 * Generates a unique ID for a time slot
 * @returns Unique string ID
 */
export function generateTimeSlotId(): string {
  return `slot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Converts minutes since midnight to 12-hour time and period
 * @param minutes - Minutes since midnight (0-1440)
 * @returns { time: string, period: 'AM' | 'PM' }
 */
export function minutesTo12Hour(minutes: number): {
  time: string
  period: 'AM' | 'PM'
} {
  let mins = minutes
  if (mins === 1440) mins = 0 // midnight
  const h24 = Math.floor(mins / 60)
  const m = mins % 60
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM'
  let h12 = h24 % 12
  if (h12 === 0) h12 = 12
  return {
    time: `${h12}:${m.toString().padStart(2, '0')}`,
    period,
  }
}
